import { ClipboardX, RotateCcw } from "lucide-react";
import * as React from "react";

import { Badge, Tooltip } from "@/components/ui";
import { cn } from "@/lib/utils";

function revisionBadgeClass(revisionCount?: number) {
  if (!revisionCount || revisionCount <= 0) return "text-muted-foreground border-border";
  if (revisionCount === 1) return "bg-amber-50 text-amber-800 border-amber-200";
  if (revisionCount === 2) return "bg-orange-50 text-orange-800 border-orange-200";
  return "bg-red-50 text-red-800 border-red-200";
}

interface VideoProjectRevisionBadgeProps {
  revisionCount?: number | null;
  className?: string;
}

export function VideoProjectRevisionBadge({ revisionCount, className }: VideoProjectRevisionBadgeProps) {
  if (!revisionCount || revisionCount <= 0) return null;

  return (
    <Tooltip>
      <Tooltip.Trigger
        render={
          <span className={cn("inline-flex", className)}>
            <Badge
              variant="outline"
              className={cn(
                "h-5 shrink-0 gap-1 px-1.5 py-0 text-[10px] font-medium shadow-none",
                revisionBadgeClass(revisionCount)
              )}
            >
              <RotateCcw className="size-2.5" />
              {revisionCount}
            </Badge>
          </span>
        }
      />
      <Tooltip.Content>{revisionCount === 1 ? "1 revision" : `${revisionCount} revisions`}</Tooltip.Content>
    </Tooltip>
  );
}

const QA_FAILURE_BADGE_CLASS = "h-5 shrink-0 gap-1 px-1.5 py-0 text-[10px] font-medium shadow-none";
const QA_FAILURE_RED_CLASS = "border-red-200 bg-red-50 text-red-800";
const QA_FAILURE_YELLOW_CLASS = "border-amber-200 bg-amber-50 text-amber-800";

function qaFailureCountLabel(qaFailureCount: number) {
  return qaFailureCount === 1 ? "1 QA failure" : `${qaFailureCount} QA failures`;
}

function qaFailedStatusLabel(qaFailureCount: number) {
  return `QA Failed (${qaFailureCount}x)`;
}

interface VideoProjectQaBadgesProps {
  qaFailed?: boolean;
  qaFailureCount?: number | null;
  showFailedLabel?: boolean;
  className?: string;
  onQaFailedClick?: () => void;
}

export function VideoProjectQaBadges({
  qaFailed = false,
  qaFailureCount,
  showFailedLabel = false,
  className,
  onQaFailedClick,
}: VideoProjectQaBadgesProps) {
  const count = qaFailureCount ?? 0;
  if (count <= 0) return null;

  const label = qaFailed && showFailedLabel ? qaFailedStatusLabel(count) : String(count);
  const tooltip = qaFailed && showFailedLabel ? qaFailedStatusLabel(count) : qaFailureCountLabel(count);
  const badge = (
    <Badge
      variant="outline"
      className={cn(QA_FAILURE_BADGE_CLASS, qaFailed ? QA_FAILURE_RED_CLASS : QA_FAILURE_YELLOW_CLASS)}
      aria-label={tooltip}
    >
      <ClipboardX className="size-2.5" />
      {label}
    </Badge>
  );

  return (
    <Tooltip>
      <Tooltip.Trigger
        render={
          onQaFailedClick ? (
            <button type="button" className={cn("inline-flex cursor-pointer", className)} onClick={onQaFailedClick}>
              {badge}
            </button>
          ) : (
            <span className={cn("inline-flex", className)}>{badge}</span>
          )
        }
      />
      <Tooltip.Content>{tooltip}</Tooltip.Content>
    </Tooltip>
  );
}
